import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TempAttachedResearch, BatchAttachRequest, EntityCreationConfig } from '../../contexts/universal-creation-research/types';

export const createBatchAttachHook = (config: EntityCreationConfig) => {
  return () => {
    return useMutation({
      mutationFn: async ({ entityId, researchAttachments }: BatchAttachRequest) => {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError || !user) {
          throw new Error('You must be logged in to attach research');
        }

        // Prepare batch insert data based on entity configuration
        const attachmentsToInsert = researchAttachments.map(({ research, summary }) => ({
          [config.attachmentColumn]: entityId,
          research_id: research.id,
          research_summary: summary,
          user_id: user.id,
        }));

        // Batch insert all research attachments
        const { data, error } = await (supabase as any)
          .from(config.attachmentTable)
          .insert(attachmentsToInsert)
          .select(`
            id,
            ${config.attachmentColumn},
            research_id,
            research_summary,
            created_at,
            research_library (
              id,
              title,
              content,
              research_type,
              created_at
            )
          `);

        if (error) {
          console.error(`Error batch attaching research to ${config.entityType}:`, error);
          throw error;
        }

        return data;
      },
      onSuccess: (data, variables) => {
        const count = variables.researchAttachments.length;
        toast.success(`${count} research item${count > 1 ? 's' : ''} attached to ${config.displayName.toLowerCase()} successfully`);
      },
      onError: (error) => {
        console.error(`Error batch attaching research to ${config.entityType}:`, error);
        toast.error(`Failed to attach research to ${config.displayName.toLowerCase()}`);
      },
    });
  };
};

// Convenience function to create batch attach hook for any entity
export const useBatchAttachResearch = (entityType: string) => {
  // This will be implemented by the factory for each entity
  // Each entity will get its own typed version
  throw new Error(`useBatchAttachResearch must be called from entity-specific hook for ${entityType}`);
}; 